import { HTMLInputTypeAttribute, useState } from 'react';
import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';

import { InputMask } from 'primereact/inputmask';
import { classNames } from 'primereact/utils';

export type FormInputMaskProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  mask: string;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  type?: HTMLInputTypeAttribute | undefined;
  toggleMask?: boolean;
};

const FormInputMask = <TFieldValues extends FieldValues>({ ...props }: FormInputMaskProps<TFieldValues>): JSX.Element => {
  const { className, control, field, errors, label, mask, disabled, placeholder, type, toggleMask } = props;
  const [showValue, setShowValue] = useState(false);

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  const getInputType = (): HTMLInputTypeAttribute | undefined => {
    if (toggleMask) {
      return showValue ? 'text' : 'password';
    }
    return type;
  };

  return (
    <div className={className ?? 'field'}>
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => {
          const inputMaskElement = (
            <InputMask
              id={field.name}
              className={classNames({ 'p-invalid': fieldState.error, disabled })}
              value={field.value ?? ''}
              onChange={(e) => (e.target.value ? field.onChange(e.target.value as any) : field.onChange(null as any))}
              mask={mask}
              disabled={disabled}
              placeholder={placeholder}
              type={getInputType()}
              autoComplete={toggleMask || type === 'password' ? 'new-password' : undefined}
              unmask={true}
            />
          );

          if (toggleMask) {
            return (
              <>
                <label
                  htmlFor={field.name}
                  className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}
                >
                  {label}
                </label>
                <div className="p-icon-field p-icon-field-right">
                  {inputMaskElement}
                  <i
                    className={`p-input-icon pi ${showValue ? 'pi-eye-slash' : 'pi-eye'} cursor-pointer`}
                    onClick={() => setShowValue(!showValue)}
                  />
                </div>
              </>
            );
          }

          return (
            <>
              <label
                htmlFor={field.name}
                className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}
              >
                {label}
              </label>
              {inputMaskElement}
            </>
          );
        }}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormInputMask;
